import "normalize.css";
import "./App.css";
import React from "react";
import ReactAudioPlayer from "react-audio-player";
import AudioPlayer from "./AudioPlayer";
import { DragSwitch } from "react-dragswitch";
import "react-dragswitch/dist/index.css";
import ReactSlider from "react-slider";
import backingTracks from "./tracks";
import { find } from "lodash";

const channels = {
  metronome: null,
  vocal: null,
  strum: null,
  practice: null,
  picking: null,
  drum: null,
  extra: null,
};

const CDN_URL = "https://pub-84b1f1af74c74e7585591df996bd5912.r2.dev";

const getChannel = (currentTrack, name) =>
  find(currentTrack.channels, (x) => x.id === name) || null;

function App() {
  const currentTrack = backingTracks.filter(
    (x) => "/" + x.slug === window.location.pathname
  );

  if (window.location.pathname === "/") {
    return (
      <div style={{ width: "100%", textAlign: "center", marginTop: 50 }}>
        Invalid Path. Please visit{" "}
        <a href="https://www.patreon.com/for3v3rfaithful">
          https://www.patreon.com/for3v3rfaithful
        </a>{" "}
        for the correct link.
      </div>
    );
  }

  if (currentTrack.length === 0) {
    return (
      <div style={{ width: "100%", textAlign: "center", marginTop: 50 }}>
        Link has expired
      </div>
    );
  }

  return <Player currentTrack={currentTrack[0]} />;
}

function Player({ currentTrack }) {
  const [metroMuted, setMetroMuted] = React.useState(false);
  const [vocalMuted, setVocalMuted] = React.useState(false);
  const [practiceMuted, setPracticeMuted] = React.useState(true);
  const [strumMuted, setStrumMuted] = React.useState(false);
  const [pickingMuted, setPickingMuted] = React.useState(true);
  const [drumMuted, setDrumMuted] = React.useState(false);
  const [extraMuted, setExtraMuted] = React.useState(false);
  const [bpm, setBpm] = React.useState(currentTrack.bpm);
  const hasPicking = !!getChannel(currentTrack, "picking");
  const hasDrum = !!getChannel(currentTrack, "drum");
  const hasExtra = !!getChannel(currentTrack, "extra");

  React.useEffect(() => {
    channels.player = document.getElementById("player");
    channels.metronome = document.getElementById("metronome");
    channels.strum = document.getElementById("strum");
    channels.vocal = document.getElementById("vocal");
    // muted by default
    channels.practice = document.getElementById("practice");
    channels.practice.volume = 0;
    channels.picking = document.getElementById("picking");
    channels.drum = document.getElementById("drum");
    channels.extra = document.getElementById("extra");
    if (hasPicking) {
      channels.picking.volume = 0;
    }
  }, [currentTrack.channels, hasPicking]);

  return (
    <div className="container">
      <div className="card" style={{ marginTop: 100, borderRadius: 12 }}>
        <h2>{currentTrack.title}</h2>
        <h3>{currentTrack.artist}</h3>
        <br />
        <div>
          <ReactAudioPlayer
            id="player"
            src={`${CDN_URL}/${currentTrack.channels[0].filename}`}
            volume={0}
            type="audio/mpeg"
            controls
            preload="auto"
            controlsList="nodownload"
            onPlay={() => {
              channels.strum.play();
              channels.practice.play();
              channels.metronome.play();
              channels.vocal.play();
              if (hasPicking) {
                channels.picking.play();
              }
              if (hasDrum) {
                channels.drum.play();
              }
              if (hasExtra) {
                channels.extra.play();
              }
            }}
            onPause={() => {
              channels.strum.pause();
              channels.practice.pause();
              channels.metronome.pause();
              channels.vocal.pause();
              if (hasPicking) {
                channels.picking.pause();
              }
              if (hasDrum) {
                channels.drum.pause();
              }
              if (hasExtra) {
                channels.extra.pause();
              }
            }}
            onSeeked={({ target: { currentTime } }) => {
              channels.strum.currentTime = currentTime;
              channels.practice.currentTime = currentTime;
              channels.metronome.currentTime = currentTime;
              channels.vocal.currentTime = currentTime;
              if (hasPicking) {
                channels.picking.currentTime = currentTime;
              }
              if (hasDrum) {
                channels.drum.currentTime = currentTime;
              }
              if (hasExtra) {
                channels.extra.currentTime = currentTime;
              }
            }}
          />

          {currentTrack.channels.map(({ id, filename }) => (
            <AudioPlayer id={id} preload="auto" key={id}>
              <source src={`${CDN_URL}/${filename}`} type="audio/mpeg" />
              Your browser does not support the audio element.
            </AudioPlayer>
          ))}
        </div>
        <br />

        <div
          style={{ width: 250, position: "relative", margin: "0 0 20px 20px" }}
        >
          <span style={{ marginRight: 42, color: "gray" }}>Half</span>
          <span>Playback Speed</span>
          <span style={{ position: "absolute", right: 0, color: "gray" }}>
            Full
          </span>
          <ReactSlider
            className="slider-horz"
            thumbClassName="slider-thumb"
            trackClassName="slider-track"
            min={50}
            max={100}
            defaultValue={100}
            onAfterChange={(speed) => {
              channels.strum.playbackRate = speed / 100;
              channels.practice.playbackRate = speed / 100;
              channels.metronome.playbackRate = speed / 100;
              channels.vocal.playbackRate = speed / 100;
              channels.player.playbackRate = speed / 100;
              if (hasPicking) {
                channels.picking.playbackRate = speed / 100;
              }
              if (hasDrum) {
                channels.drum.playbackRate = speed / 100;
              }
              if (hasExtra) {
                channels.extra.playbackRate = speed / 100;
              }
              setBpm(Math.round(currentTrack.bpm * (speed / 100)));
            }}
          />
          <div>BPM: {bpm}</div>
        </div>

        <div style={{ paddingLeft: 20 }}>
          <label style={{ marginBottom: 15, display: "block" }}>
            <DragSwitch
              checked={!metroMuted}
              onChange={() => {
                setMetroMuted(!metroMuted);
                !metroMuted
                  ? (channels.metronome.volume = 0)
                  : (channels.metronome.volume = 1);
              }}
            />
            Metronome
          </label>

          <label style={{ marginBottom: 15, display: "block" }}>
            <DragSwitch
              checked={!vocalMuted}
              onChange={() => {
                setVocalMuted(!vocalMuted);
                !vocalMuted
                  ? (channels.vocal.volume = 0)
                  : (channels.vocal.volume = 1);
              }}
            />
            Vocals
          </label>

          <label style={{ marginBottom: 15, display: "block" }}>
            <DragSwitch
              checked={!strumMuted}
              onChange={() => {
                setStrumMuted(!strumMuted);
                !strumMuted
                  ? (channels.strum.volume = 0)
                  : (channels.strum.volume = 1);
              }}
            />
            Strumming
          </label>

          {hasPicking && (
            <label style={{ marginBottom: 15, display: "block" }}>
              <DragSwitch
                checked={!pickingMuted}
                onChange={() => {
                  setPickingMuted(!pickingMuted);
                  !pickingMuted
                    ? (channels.picking.volume = 0)
                    : (channels.picking.volume = 1);
                }}
              />
              Picking
            </label>
          )}

          <label style={{ marginBottom: 15, display: "block" }}>
            <DragSwitch
              checked={!practiceMuted}
              onChange={() => {
                setPracticeMuted(!practiceMuted);
                !practiceMuted
                  ? (channels.practice.volume = 0)
                  : (channels.practice.volume = 1);
              }}
            />
            Practice Strum
          </label>

          {hasDrum && (
            <label style={{ marginBottom: 15, display: "block" }}>
              <DragSwitch
                checked={!drumMuted}
                onChange={() => {
                  setDrumMuted(!drumMuted);
                  !drumMuted
                    ? (channels.drum.volume = 0)
                    : (channels.drum.volume = 0.75);
                }}
              />
              Drums
            </label>
          )}

          {hasExtra && (
            <label style={{ marginBottom: 15, display: "block" }}>
              <DragSwitch
                checked={!extraMuted}
                onChange={() => {
                  setExtraMuted(!extraMuted);
                  !extraMuted
                    ? (channels.extra.volume = 0)
                    : (channels.extra.volume = 0.75);
                }}
              />
              {getChannel(currentTrack, "extra")?.label || "Extra"}
            </label>
          )}
        </div>

        <p>
          Please note, playback on iOS is currently not supported but we are
          working to fix this soon! However, you can use this on most desktop
          browsers and Android in the mean time.
        </p>
      </div>
    </div>
  );
}

export default App;
